import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import {
    Box,
    Button,
    Card,
    CardContent,
    CardActions,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Stack,
    Typography,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Alert
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import DoctorIcon from '@mui/icons-material/LocalHospital'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import LaunchIcon from '@mui/icons-material/Launch'
import ArticleIcon from '@mui/icons-material/Article'
import FolderIcon from '@mui/icons-material/Folder'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { ConsultationStatus } from '@/core/models/consultation.model'
import { ReportViewer } from '@/pages/consultation/components/ReportViewer'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

interface ConsultationCardProps {
    consultationId: string
}

export const ConsultationCard: React.FC<ConsultationCardProps> = ({ consultationId }) => {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState(0)
    const authUser = useAppSelector(selectAuthUser)

    const { data: consultation, error, isLoading } = useGetConsultationQuery(consultationId)

    const handleCardClick = () => {
        window.open(`/consultations/${consultationId}`, '_blank')
    }

    if (isLoading) {
        return (
            <Card>
                <CardContent>
                    <Stack alignItems="center" justifyContent="center" minHeight={150} spacing={2}>
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            {t('page.medconnectAgent.consultationCard.loading')}
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>
        )
    }

    if (error || !consultation) {
        return (
            <Card>
                <CardContent>
                    <Alert severity="error">
                        {error ? t('page.medconnectAgent.consultationCard.loadError') : t('page.medconnectAgent.consultationCard.notFound')}
                    </Alert>
                </CardContent>
            </Card>
        )
    }

    const formattedDate = dayjs(consultation.startDate).format('DD/MM/YYYY HH:mm')
    const dateText = dayjs(consultation.startDate).isSame(dayjs(), 'day')
        ? t('page.medconnectAgent.consultationCard.todayAt', { time: dayjs(consultation.startDate).format('HH:mm') })
        : formattedDate

    const doctorName = consultation.medecin
        ? `${consultation.medecin.userInfo?.name} ${consultation.medecin.userInfo?.surname}`.trim()
        : t('page.medconnectAgent.consultationCard.noDoctor')

    const patientName = consultation.patient
        ? `${consultation.patient.userInfo?.name} ${consultation.patient.userInfo?.surname}`.trim()
        : t('page.medconnectAgent.consultationCard.unknownPatient')

    return (
        <Card
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 3,
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <CardContent onClick={handleCardClick} sx={{ cursor: 'pointer' }}>
                <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">
                            {t('page.medconnectAgent.consultationCard.title')}
                        </Typography>
                        <Chip
                            label={t(`common:model.consultation.status.${consultation.status}.label`, consultation.status)}
                            color={
                                consultation.status === ConsultationStatus.Completed ? 'success' :
                                    consultation.status === ConsultationStatus.Canceled ? 'error' :
                                        consultation.status === ConsultationStatus.Accepted ? 'primary' :
                                            consultation.status === ConsultationStatus.Scheduled ? 'info' : 'default'
                            }
                            size="small"
                        />
                    </Stack>

                    <Divider />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <CalendarTodayIcon fontSize="small" color="action" />
                                <Typography variant="body2">{dateText}</Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <DoctorIcon fontSize="small" color="action" />
                                <Typography variant="body2">{doctorName}</Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <PersonIcon fontSize="small" color="action" />
                                <Typography variant="body2">{patientName}</Typography>
                            </Stack>
                        </Grid>
                        {consultation.reason && (
                            <Grid item xs={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <ArticleIcon fontSize="small" color="action" />
                                    <Typography variant="body2">{consultation.reason}</Typography>
                                </Stack>
                            </Grid>
                        )}
                    </Grid>

                    <Box sx={{ width: '100%', mt: 2 }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, newValue) => setActiveTab(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab label={t('page.medconnectAgent.consultationCard.details')} />
                            {/* {!!consultation.report && <Tab label={t('Report', 'Report')} />} */}
                            {!!consultation.mesures?.length && <Tab label={t('page.medconnectAgent.consultationCard.measurements')} />}
                            {!!consultation.biologicals?.length && <Tab label={t('page.medconnectAgent.consultationCard.biologicals')} />}
                            {!!consultation.files?.length && <Tab label={t('page.medconnectAgent.consultationCard.files')} />}
                        </Tabs>

                        <Box sx={{ p: 2, pt: 3 }}>
                            {activeTab === 0 && (
                                <Typography variant="body2">{consultation.commentaire || t('page.medconnectAgent.consultationCard.noDetails')}</Typography>
                            )}
                            {/* {activeTab === 1 && consultation.report && (
                                <ReportViewer consultation={consultation} userId={authUser?._id} />
                            )} */}
                            {activeTab === 2 && (
                                <Table size="small">
                                    <TableBody>
                                        {consultation.mesures?.map((measure) => (
                                            <TableRow key={measure.label}>
                                                <TableCell sx={{ fontWeight: 'medium' }}>{measure.label}</TableCell>
                                                <TableCell>{measure.value} {measure.unit}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                            {activeTab === 3 && (
                                <Table size="small">
                                    <TableBody>
                                        {consultation.biologicals?.map((bio) => (
                                            <TableRow key={bio.id}>
                                                <TableCell>
                                                    <Typography variant="subtitle2">{bio.label}</Typography>
                                                    {bio.comment && <Typography variant="caption" color="text.secondary">{bio.comment}</Typography>}
                                                </TableCell>
                                                <TableCell>{bio.value} {bio.unit}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                            {activeTab === 4 && (
                                <Stack spacing={1}>
                                    {consultation.files?.map((file) => (
                                        <Button
                                            key={file.key}
                                            variant="outlined"
                                            startIcon={<FolderIcon />}
                                            size="small"
                                            onClick={() => window.open(file.url, '_blank')}
                                            sx={{ justifyContent: 'flex-start' }}
                                        >
                                            {file.filename}
                                        </Button>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Box>
                </Stack>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
                <Button size="small" startIcon={<LaunchIcon />} onClick={handleCardClick}>
                    {t('page.medconnectAgent.consultationCard.open')}
                </Button>
            </CardActions>
        </Card>
    )
}

export default ConsultationCard 